import { CoursesCompleteDto } from '../generated-types/coursesCompleteDto';
import { EditManagersInfoDto } from '../generated-types/editManagersInfoDto';
import { LicenseStateDto } from '../generated-types/licenseStateDto';
import { MembersGridLineDto } from '../generated-types/membersGridLineDto';
import { RequiredTrainingDto } from '../generated-types/requiredTrainingDto';
import { SubscriptionStatusDto } from '../generated-types/subscriptionStatusDto';
import { AbstractApi } from './AbstractApi';

/**
 * An api to interact with the management controller
 */
export default class ManagementApi extends AbstractApi {
  /**
   * Invite the members to the training
   * @param members The members to invite
   */
  async InviteMembers(
    members: string
  ): Promise<{ code: number; body: string }> {
    const response = await this.authFetch(
      this.BuildUrl('management/invite-members'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(members),
      }
    );

    return {
      code: response.status,
      body: await response.text(),
    };
  }

  /**
   * Get the members for the manager's organization
   */
  async GetMembers(): Promise<MembersGridLineDto[]> {
    const response = await this.authFetch(this.BuildUrl('management/members'), {
      method: 'GET',
    });

    if (!response.ok) throw Error();

    return (await response.json()) as MembersGridLineDto[];
  }

  /**
   * Get the members for the organization
   * @param orgId The id of the organization to get the members for
   */
  async GetMembersWithOrgId(orgId: string): Promise<MembersGridLineDto[]> {
    const response = await this.authFetch(
      this.BuildUrl(`management/members/${orgId}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as MembersGridLineDto[];
  }

  /**
   * Remove expected members from the organization
   * @param memberIds The IDs of the expected members to remove
   */
  async RemoveExpectedMembers(memberIds: string[]): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('management/expected-members'),
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(memberIds),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to remove members: ${response.statusText}`);
    }
  }

  /**
   * Get the name of the manager's organization.
   * @returns The name of the organization.
   */
  async GetOrganizationName(): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('management/organization-name'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch organization name');
    }

    return await response.text();
  }

  /**
   * Get the name of the specified organization.
   * @param orgId The id of the organization to get the names for
   * @returns The name of the organization.
   */
  async GetOrganizationNameWithOrgId(orgId: String): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl(`management/organization-name/${orgId}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch organization name');
    }

    return await response.text();
  }

  /**
   * Get the yearly lapse date of the manager's organization.
   * @returns The yearly lapse date
   */
  async GetYearlyLapseDate(): Promise<Date> {
    const response = await this.authFetch(
      this.BuildUrl('management/yearly-lapse-date'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch yearly lapse date');
    }

    return new Date(await response.json());
  }

  /**
   * Get the yearly lapse date of the manager's organization.
   * @param orgId The id of the organization to get the yearly lapse date for
   * @returns The yearly lapse date
   */
  async GetYearlyLapseDateWithOrgId(orgId: String): Promise<Date> {
    const response = await this.authFetch(
      this.BuildUrl(`management/yearly-lapse-date/${orgId}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch yearly lapse date');
    }

    return new Date(await response.json());
  }

  /**
   * Get the required courses completion status for the manager's organization.
   * @param orgId The id of the organization to get the courses complete for
   * @returns The courses completion details.
   */
  async GetCoursesCompleteWithOrgId(
    orgId: string
  ): Promise<CoursesCompleteDto> {
    const response = await this.authFetch(
      this.BuildUrl(`management/courses-complete/${orgId}`),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch courses completion data');
    }

    return (await response.json()) as CoursesCompleteDto;
  }

  /**
   * Get the required courses completion status for the manager's organization.
   * @returns The courses completion details.
   */
  async GetCoursesComplete(): Promise<CoursesCompleteDto> {
    const response = await this.authFetch(
      this.BuildUrl('management/courses-complete'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch courses completion data');
    }

    return (await response.json()) as CoursesCompleteDto;
  }

  /**
   * Retrieve the state of the licenses for an organization
   * @returns The state of the licenses
   */
  async GetLicenseQuantity(): Promise<LicenseStateDto> {
    const response = await this.authFetch(
      this.BuildUrl('management/licenses/state'),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch the license state`);
    }

    return (await response.json()) as LicenseStateDto;
  }

  /**
   * Retrieve the state of the licenses for an organization
   * @param orgId The id of the organization to get the license quantity for
   * @returns The state of the licenses
   */
  async GetLicenseQuantityWithOrgId(orgId: string): Promise<LicenseStateDto> {
    const response = await this.authFetch(
      this.BuildUrl(`management/licenses/state/${orgId}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch the license state`);
    }

    return (await response.json()) as LicenseStateDto;
  }

  /**
   * Retrieves details about the subscription status
   * @param orgId The id of the organization to get the subscription status for
   * @returns The subscription status details
   */
  async GetSubscriptionStatusWithOrgId(
    orgId: String
  ): Promise<SubscriptionStatusDto> {
    const response = await this.authFetch(
      this.BuildUrl(`management/subscription/status/${orgId}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch subscription status: ${response.statusText}`
      );
    }

    const licenseRenewalDateDto =
      (await response.json()) as SubscriptionStatusDto;
    licenseRenewalDateDto.endOfPeriod = new Date(
      licenseRenewalDateDto.endOfPeriod as any
    );

    return licenseRenewalDateDto;
  }

  /**
   * Retrieves details about the subscription status
   * @returns The subscription status details
   */
  async GetSubscriptionStatus(): Promise<SubscriptionStatusDto> {
    const response = await this.authFetch(
      this.BuildUrl('management/subscription/status'),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch subscription status: ${response.statusText}`
      );
    }

    const licenseRenewalDateDto =
      (await response.json()) as SubscriptionStatusDto;
    licenseRenewalDateDto.endOfPeriod = new Date(
      licenseRenewalDateDto.endOfPeriod as any
    );

    return licenseRenewalDateDto;
  }

  /**
   * Get the organization's required trainings.
   */
  async GetRequiredTrainings(): Promise<string[]> {
    const response = await this.authFetch(
      this.BuildUrl('management/required-trainings'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw new Error('Failed to fetch required trainings');

    return (await response.json()) as string[];
  }

  /**
   * Update the organization's required trainings.
   * @param requiredTrainingDto The required training dto
   */
  async UpdateRequiredTrainings(
    requiredTrainingDto: RequiredTrainingDto
  ): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('management/required-trainings'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requiredTrainingDto),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update required trainings');
    }
  }
  /**
   * Get the information needed in order to edit the managers
   */
  async GetEditManagersInfo(): Promise<EditManagersInfoDto> {
    const response = await this.authFetch(
      this.BuildUrl('management/edit-managers-info'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get edit managers info');
    }

    return (await response.json()) as EditManagersInfoDto;
  }

  /**
   * Update the list of managers in the organization.
   */
  async UpdateManagers(managerIds: string[]): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('management/update-managers'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(managerIds),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update managers');
    }
  }
}
